import { useState, useRef, useCallback, useEffect } from 'react';

const MAX_SECONDS = 15;

export interface VoiceRecorder {
  isRecording: boolean;
  seconds: number;
  recordedBlob: Blob | null;
  recordedUrl: string;
  error: string;
  maxSeconds: number;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
}

/**
 * Microphone recorder for short voice clips. Auto-stops at MAX_SECONDS.
 * Produces a raw (webm/mp4) Blob; convert to MP3 separately before upload.
 */
export function useVoiceRecorder(): VoiceRecorder {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const [error, setError] = useState('');

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const urlRef = useRef('');

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  };

  const stop = useCallback(() => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const start = useCallback(async () => {
    setError('');
    setRecordedBlob(null);
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = '';
    }
    setRecordedUrl('');
    setSeconds(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setRecordedUrl(url);
        stopStream();
      };

      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setSeconds(prev => {
          const next = prev + 1;
          if (next >= MAX_SECONDS) {
            clearTimer();
            if (recorderRef.current && recorderRef.current.state !== 'inactive') {
              recorderRef.current.stop();
            }
            setIsRecording(false);
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      console.error('Recording error:', e);
      setError('Microphone access was denied or is unavailable.');
      stopStream();
      clearTimer();
      setIsRecording(false);
    }
  }, []);

  const reset = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = '';
    }
    setRecordedBlob(null);
    setRecordedUrl('');
    setSeconds(0);
    setError('');
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    clearTimer();
    stopStream();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  return { isRecording, seconds, recordedBlob, recordedUrl, error, maxSeconds: MAX_SECONDS, start, stop, reset };
}
